/**
 * CALCULADORA 01 — API 570
 * Thickness loss over time: corrosion rates and remaining life.
 *
 * This calculator NEVER computes a minimum required thickness — it receives
 * one (which may come from the ASME B31.3 calculator, by an explicit action).
 * It shares no formula with the NACE/AMPP or ASME B31.3 calculators.
 */

export type Api570RateBasis = "SHORT_TERM" | "LONG_TERM" | "HIGHEST" | "MANUAL";

export interface Api570Inputs {
  /** Thickness at the first (baseline) measurement, in mm. */
  initialThicknessMm: number | null;
  initialDate: string;
  /** Thickness at the previous measurement, in mm. */
  previousThicknessMm: number | null;
  previousDate: string;
  currentThicknessMm: number | null;
  currentDate: string;
  /** Received, never calculated here. */
  minimumRequiredThicknessMm: number | null;
  rateBasis: Api570RateBasis;
  /** Only used when rateBasis is MANUAL. */
  manualRateMmPerYear?: number | null;
  /**
   * Required whenever the adopted rate is not the conservative reference
   * (the highest of short and long term) — the departure has to be justified
   * by the professional, not silently accepted.
   */
  rateBasisJustification?: string;
}

export interface Api570Result {
  shortTermRateMmPerYear: number | null;
  longTermRateMmPerYear: number | null;
  /** Highest of short and long term — the conservative reference API 570 suggests. */
  conservativeRateMmPerYear: number | null;
  adoptedRateMmPerYear: number | null;
  adoptedRateBasis: Api570RateBasis;
  /** true when the adopted rate departs from the conservative reference. */
  departsFromConservativeReference: boolean;
  currentThicknessMm: number | null;
  minimumRequiredThicknessMm: number | null;
  remainingLifeYears: number | null;
  /** Date the current thickness is projected to reach the minimum, ISO yyyy-mm-dd. */
  estimatedDateAtMinimumThickness: string | null;
  warnings: string[];
}

const DAYS_PER_YEAR = 365.25;

/** Whole-day difference between two civil dates, expressed in years. Timezone-safe. */
export function yearsBetween(fromDate: string, toDate: string): number | null {
  const from = parseCivilDate(fromDate);
  const to = parseCivilDate(toDate);
  if (from === null || to === null) return null;
  const days = (to - from) / (1000 * 60 * 60 * 24);
  return days / DAYS_PER_YEAR;
}

/** Parses "yyyy-mm-dd" as a civil date in UTC, so no timezone can shift the day. */
function parseCivilDate(value: string | null | undefined): number | null {
  const raw = (value ?? "").trim();
  if (!raw) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (match) {
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(raw);
  return isNaN(parsed.getTime()) ? null : parsed.getTime();
}

function addYearsToCivilDate(baseDate: string, years: number): string | null {
  const base = parseCivilDate(baseDate);
  if (base === null || !Number.isFinite(years)) return null;
  const target = new Date(base + years * DAYS_PER_YEAR * 24 * 60 * 60 * 1000);
  if (isNaN(target.getTime())) return null;
  const y = target.getUTCFullYear();
  const m = String(target.getUTCMonth() + 1).padStart(2, "0");
  const d = String(target.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function rateBetween(
  earlierThickness: number | null,
  earlierDate: string,
  laterThickness: number | null,
  laterDate: string
): number | null {
  if (earlierThickness === null || laterThickness === null) return null;
  if (!Number.isFinite(earlierThickness) || !Number.isFinite(laterThickness)) return null;
  const years = yearsBetween(earlierDate, laterDate);
  if (years === null || years <= 0) return null;
  return (earlierThickness - laterThickness) / years;
}

/**
 * CR_ST = (previous − current) / years(previous → current)
 * CR_LT = (initial  − current) / years(initial  → current)
 * RL    = (current  − minimum) / adopted rate
 */
export function calculateApi570(inputs: Api570Inputs): Api570Result {
  const warnings: string[] = [];

  const shortTerm = rateBetween(inputs.previousThicknessMm, inputs.previousDate, inputs.currentThicknessMm, inputs.currentDate);
  const longTerm = rateBetween(inputs.initialThicknessMm, inputs.initialDate, inputs.currentThicknessMm, inputs.currentDate);

  if (shortTerm === null) warnings.push("Taxa de curto prazo não calculada: informe espessura e data da medição anterior.");
  if (longTerm === null) warnings.push("Taxa de longo prazo não calculada: informe espessura e data da medição inicial.");

  const conservativeCandidates = [shortTerm, longTerm].filter((r): r is number => r !== null);
  const conservative = conservativeCandidates.length > 0 ? Math.max(...conservativeCandidates) : null;

  let adopted: number | null = null;
  switch (inputs.rateBasis) {
    case "SHORT_TERM":
      adopted = shortTerm;
      break;
    case "LONG_TERM":
      adopted = longTerm;
      break;
    case "MANUAL":
      adopted = Number.isFinite(inputs.manualRateMmPerYear ?? NaN) ? (inputs.manualRateMmPerYear as number) : null;
      break;
    case "HIGHEST":
    default:
      adopted = conservative;
      break;
  }

  // Adotar uma taxa menor que a referência conservadora é uma decisão do
  // profissional e precisa vir justificada.
  const departs =
    adopted !== null && conservative !== null && Math.abs(adopted - conservative) > 1e-12;

  if (departs && !inputs.rateBasisJustification?.trim()) {
    warnings.push("Taxa adotada diferente da referência conservadora sem justificativa técnica registrada.");
  }

  let remainingLife: number | null = null;
  let estimatedDate: string | null = null;

  const current = inputs.currentThicknessMm;
  const minimum = inputs.minimumRequiredThicknessMm;

  if (current === null || minimum === null) {
    warnings.push("Vida remanescente não calculada: informe a espessura atual e a espessura mínima requerida.");
  } else if (adopted === null) {
    warnings.push("Vida remanescente não calculada: nenhuma taxa de corrosão disponível.");
  } else if (adopted <= 0) {
    // No measurable loss: remaining life is not a finite number, and
    // reporting a huge placeholder would be misleading.
    warnings.push("Taxa adotada menor ou igual a zero: não há perda de espessura mensurável no período.");
  } else {
    remainingLife = (current - minimum) / adopted;
    estimatedDate = addYearsToCivilDate(inputs.currentDate, remainingLife);
    if (remainingLife < 0) {
      warnings.push("Espessura atual abaixo da mínima requerida: vida remanescente negativa.");
    }
  }

  return {
    shortTermRateMmPerYear: shortTerm,
    longTermRateMmPerYear: longTerm,
    conservativeRateMmPerYear: conservative,
    adoptedRateMmPerYear: adopted,
    adoptedRateBasis: inputs.rateBasis,
    departsFromConservativeReference: departs,
    currentThicknessMm: current,
    minimumRequiredThicknessMm: minimum,
    remainingLifeYears: remainingLife,
    estimatedDateAtMinimumThickness: estimatedDate,
    warnings
  };
}
